import { useState, useEffect } from 'react';
import { FiSearch, FiX, FiCheck } from 'react-icons/fi';
import { chatService } from '../../services/chatApi';
import { useChat } from '../../contexts/ChatContext';
import Avatar from './Avatar';
import { cn } from '../../utils/helpers';

export default function MemberPicker({ onDone }) {
  const { startConversation } = useChat();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await chatService.searchDirectory(query);
        if (!cancelled) setResults(response.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  const toggle = (person) => {
    setSelected((prev) =>
      prev.some((p) => p.id === person.id)
        ? prev.filter((p) => p.id !== person.id)
        : [...prev, person]
    );
  };

  const create = async () => {
    if (selected.length === 0 || creating) return;
    setCreating(true);
    setError(null);

    try {
      await startConversation({
        // One person with no name is a DM, which the server makes idempotent —
        // picking the same person twice reopens the existing conversation.
        type: selected.length === 1 && !groupName.trim() ? 'dm' : 'group',
        memberIds: selected.map((p) => p.id),
        name: groupName.trim() || undefined,
      });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="p-3 border-b border-neutral-300 space-y-2">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/35" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search members…"
            autoFocus
            className="w-full pl-9 pr-3 py-2 rounded-[10px] border border-neutral-300 bg-white text-sm text-primary
                       placeholder:text-primary/35 focus:outline-none focus:ring-2
                       focus:ring-accent/40 focus:border-accent transition-all"
          />
        </div>

        {selected.length > 0 && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {selected.map((person) => (
                <span
                  key={person.id}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full
                             bg-highlight border border-primary/15 text-primary text-xs"
                >
                  {person.displayName}
                  <button
                    onClick={() => toggle(person)}
                    className="p-0.5 rounded-full text-primary/60 hover:text-primary hover:bg-primary/10 transition-colors"
                    aria-label={`Remove ${person.displayName}`}
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            {selected.length > 1 && (
              <input
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                placeholder="Group name (optional)"
                maxLength={140}
                className="w-full px-3 py-2 rounded-[10px] border border-neutral-300 bg-white text-sm text-primary
                           placeholder:text-primary/35 focus:outline-none focus:ring-2
                           focus:ring-accent/40 focus:border-accent transition-all"
              />
            )}
          </>
        )}
      </div>

      <ul className="flex-1 overflow-y-auto divide-y divide-neutral-300">
        {searching && results.length === 0 && (
          <li className="px-4 py-3 text-sm text-primary/40">Searching…</li>
        )}
        {!searching && results.length === 0 && (
          <li className="px-4 py-3 text-sm text-primary/40">No members found.</li>
        )}

        {results.map((person) => {
          const isSelected = selected.some((p) => p.id === person.id);
          return (
            <li key={person.id}>
              <button
                onClick={() => toggle(person)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-highlight/40 transition-colors text-left"
              >
                <Avatar name={person.displayName} photoUrl={person.photoUrl} seed={person.id} />

                <span className="flex-1 text-sm text-primary/85 truncate">{person.displayName}</span>

                <span
                  className={cn(
                    'w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors',
                    isSelected ? 'bg-accent border-accent text-primary' : 'border-primary/25'
                  )}
                >
                  {isSelected && <FiCheck className="w-3 h-3" />}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {error && <p className="px-4 py-2 text-xs text-alert">{error}</p>}

      <div className="p-3 border-t border-neutral-300">
        <button
          onClick={create}
          disabled={selected.length === 0 || creating}
          className={cn(
            'w-full py-2.5 rounded-[10px] text-sm font-semibold transition-all',
            selected.length === 0 || creating
              ? 'bg-primary/10 text-primary/30 cursor-not-allowed'
              : 'bg-accent text-primary shadow-sm hover:bg-accent-600 hover:shadow-md active:scale-[0.99]'
          )}
        >
          {creating
            ? 'Starting…'
            : selected.length > 1
              ? `Start group (${selected.length})`
              : 'Start conversation'}
        </button>
      </div>
    </div>
  );
}
